import { useMemo } from 'react';
import { remark } from 'remark';
import remarkHtml from 'remark-html';

// Utils
import cn from '../../../../utils/cn';
// Styles
import styles from './SimpleMarkdownCell.module.css';

/**
 * Simple markdown cell component for SimpleTable
 * @param {object} props
 * @param {string} [props.value] - Markdown content to render
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.placeholder='-'] - Placeholder for empty values
 * @param {boolean} [props.inline=false] - Whether to render inline markdown
 * @param {'left' | 'center' | 'right'} [props.align='left'] - Text alignment
 * @param {boolean} [props.bold=false] - Text in bold
 * @param {boolean} [props.italic=false] - Text in italic
 * @returns {JSX.Element}
 */
function SimpleMarkdownCell({
  value,
  className = '',
  placeholder = '',
  inline = false,
  align = 'left',
  bold = false,
  italic = false,
}) {
  const htmlContent = useMemo(() => {
    if (!value || value === null || value === undefined || value === '') {
      return placeholder;
    }

    try {
      const processed = remark()
        .use(remarkHtml, { sanitize: true })
        .processSync(value);

      return processed.toString();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error processing markdown:', error);
      return value;
    }
  }, [value, placeholder]);

  const displayValue = value === null || value === undefined || value === '' ? placeholder : htmlContent;
  const isEmpty = value === null || value === undefined || value === '';

  return (
    <div className={cn([
      styles['simple-markdown-cell'],
      inline && styles['simple-markdown-cell-inline'],
      className,
      bold && styles['simple-markdown-cell-bold'],
      italic && styles['simple-markdown-cell-italic'],
      align && styles[`simple-markdown-cell-align-${align}`],
    ])}
    >
      {isEmpty ? (
        <span className={styles['simple-markdown-cell-empty']}>{placeholder}</span>
      ) : (
        <div
          className={styles['simple-markdown-cell-content']}
          title={value}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: displayValue }}
        />
      )}
    </div>
  );
}

export default SimpleMarkdownCell;
